import { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import FilterBar from "../components/FilterBar";
import Table from "../components/Table";
import { fetchCategories } from "../api/categories";
import { fetchSuppliers } from "../api/suppliers";
import { bulkArchiveProducts, bulkRestoreProducts } from "../api/products";

const EXPIRY_WINDOW_DAYS = 60;
const MS_PER_DAY = 1000 * 60 * 60 * 24;
const PAGE_SIZE_OPTIONS = [20, 50, 100];

function InventoryPage({ config = {}, status, onRowClick, onRowsUpdate, onNotify, onOpenBarcodeModal }) {
  const isLoading = status?.loading;
  const hasError = Boolean(status?.error);
  const filters = useMemo(() => config.filters || [], [config.filters]);
  const [query, setQuery] = useState("");
  const rows = useMemo(() => config.rows || [], [config.rows]);
  const [localRows, setLocalRows] = useState(rows);
  const [categoryLookup, setCategoryLookup] = useState(new Map());
  const [supplierLookup, setSupplierLookup] = useState(new Map());
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);
  const filterOptions = useMemo(
    () => (filters.length ? filters : ["All", "Available", "Low", "Out of stock", "Expiring soon", "Expired"]),
    [filters]
  );
  const [statusFilters, setStatusFilters] = useState(() =>
    filterOptions.includes("All") ? ["All"] : filterOptions[0] ? [filterOptions[0]] : []
  );
  const normalizeFilterValue = useCallback((option) => String(option || "").toLowerCase(), []);
  const isAvailabilityFilter = useCallback(
    (option) => ["all", "available", "low", "out of stock", "inactive"].includes(normalizeFilterValue(option)),
    [normalizeFilterValue]
  );
  const isExpiryFilter = useCallback(
    (option) => ["expiring soon", "expired"].includes(normalizeFilterValue(option)),
    [normalizeFilterValue]
  );
  const availabilityOptions = useMemo(
    () => filterOptions.filter(isAvailabilityFilter),
    [filterOptions, isAvailabilityFilter]
  );
  const expiryOptions = useMemo(() => filterOptions.filter(isExpiryFilter), [filterOptions, isExpiryFilter]);
  const [inventoryView, setInventoryView] = useState("active");
  const [selection, setSelection] = useState({ active: new Set(), archived: new Set() });
  const [selectionRows, setSelectionRows] = useState({ active: [], archived: [] });
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [showExportMenu, setShowExportMenu] = useState(false);
  const notify = useCallback(
    (message, tone = "success") => {
      if (onNotify) onNotify(message, tone);
    },
    [onNotify]
  );

  useEffect(() => {
    if (!filterOptions.length) {
      setStatusFilters([]);
      return;
    }
    setStatusFilters((prev) => {
      const next = prev.filter((option) => filterOptions.includes(option));
      if (!next.length && filterOptions.includes("All")) return ["All"];
      return next;
    });
  }, [filterOptions]);

  const isArchivedRow = useCallback((row) => {
    const product = row?.product ?? row;
    return Boolean(product?.archivedAt) || product?.lifecycleStatus === "inactive" || product?.status === "inactive";
  }, []);

  const matchesQuery = useCallback(
    (row) => {
      const value = query.trim().toLowerCase();
      if (!value) return true;
      const haystack = [
        row.name,
        row.refCode,
        row.sku,
        row.barcode,
        row?.item?.title,
        row?.item?.subtitle,
      ]
        .filter(Boolean)
        .map((entry) => String(entry).toLowerCase());

      return haystack.some((entry) => entry.includes(value));
    },
    [query]
  );

  const getExpiryDate = useCallback((row) => {
    const product = row?.product ?? row;
    const raw = product?.expiryDate || row?.expiryDate || row?.expiry;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  }, []);

  const normalizeNumber = useCallback((value) => {
    const num = Number(value);
    return Number.isNaN(num) ? 0 : num;
  }, []);

  const getAvailability = useCallback(
    (row) => {
      if (!row) return "available";
      if (isArchivedRow(row)) return "inactive";
      const product = row.product ?? row;
      const quantity = normalizeNumber(product?.quantityOnHand ?? product?.quantity ?? row?.quantity);
      const reorderLevel = normalizeNumber(product?.reorderLevel ?? 0);
      const availabilityHint = (row.availability || product.availability || "").toString().toLowerCase();
      const statusText = (row?.status?.label || product?.status || availabilityHint || "").toString().toLowerCase();
      const normalizeStatusText = (value) => value.replace(/[\s_-]+/g, " ").trim();
      const normalizedStatus = normalizeStatusText(statusText);
      const normalizedHint = normalizeStatusText(availabilityHint);

      if (normalizedStatus.includes("inactive") || normalizedHint.includes("inactive")) return "inactive";
      if (quantity <= 0) return "out of stock";
      if (normalizedHint.includes("out of stock") || normalizedHint === "out") return "out of stock";
      if (normalizedStatus.includes("out of stock") || normalizedStatus === "out") return "out of stock";
      if (normalizedHint.includes("low") || normalizedStatus.includes("low")) return "low";
      if (reorderLevel > 0 && quantity <= reorderLevel) return "low";
      return "available";
    },
    [isArchivedRow, normalizeNumber]
  );

  const toStatusCell = useCallback((availability) => {
    if (availability === "inactive") return { type: "status", label: "inactive", tone: "warn" };
    if (availability === "out of stock") return { type: "status", label: "out of stock", tone: "danger" };
    if (availability === "low") return { type: "status", label: "low", tone: "danger" };
    return { type: "status", label: "in stock", tone: "success" };
  }, []);

  const availabilityFilters = useMemo(() => {
    const allowed = new Set();
    statusFilters.forEach((option) => {
      const lower = normalizeFilterValue(option);
      if (isAvailabilityFilter(option)) allowed.add(lower);
    });
    return allowed;
  }, [isAvailabilityFilter, normalizeFilterValue, statusFilters]);

  const expiryFilters = useMemo(() => {
    const allowed = new Set();
    statusFilters.forEach((option) => {
      const lower = normalizeFilterValue(option);
      if (isExpiryFilter(option)) allowed.add(lower);
    });
    return allowed;
  }, [isExpiryFilter, normalizeFilterValue, statusFilters]);

  const matchesStatusFilter = useCallback(
    (row) => {
      if (statusFilters.length === 0 || statusFilters.includes("All")) return true;

      const availabilityMatch = (() => {
        if (availabilityFilters.size === 0 || availabilityFilters.has("all")) return true;
        const availability = getAvailability(row);
        return availabilityFilters.has(availability);
      })();

      const expiryMatch = (() => {
        if (expiryFilters.size === 0) return true;
        const expiryDate = getExpiryDate(row);
        if (!expiryDate) return false;
        const diffDays = (expiryDate.getTime() - Date.now()) / MS_PER_DAY;
        const isExpired = diffDays < 0;
        const isExpiringSoon = diffDays >= 0 && diffDays <= EXPIRY_WINDOW_DAYS;
        return (
          (expiryFilters.has("expired") && isExpired) ||
          (expiryFilters.has("expiring soon") && isExpiringSoon)
        );
      })();

      return availabilityMatch && expiryMatch;
    },
    [availabilityFilters, expiryFilters, getAvailability, getExpiryDate, statusFilters]
  );

  const activeRows = useMemo(() => localRows.filter((row) => !isArchivedRow(row)), [localRows, isArchivedRow]);
  const archivedRows = useMemo(() => localRows.filter((row) => isArchivedRow(row)), [localRows, isArchivedRow]);

  const searchedActiveRows = useMemo(() => activeRows.filter(matchesQuery), [activeRows, matchesQuery]);
  const searchedArchivedRows = useMemo(() => archivedRows.filter(matchesQuery), [archivedRows, matchesQuery]);

  const filteredActiveRows = useMemo(
    () => searchedActiveRows.filter(matchesStatusFilter),
    [searchedActiveRows, matchesStatusFilter]
  );

  const filteredArchivedRows = useMemo(
    () => searchedArchivedRows.filter(matchesStatusFilter),
    [searchedArchivedRows, matchesStatusFilter]
  );

  const filteredRows = inventoryView === "archived" ? filteredArchivedRows : filteredActiveRows;
  const totalRows = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage) || 1);
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, currentPage, rowsPerPage]);
  const exportRows = filteredRows;
  const pageStart = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const pageEnd = totalRows === 0 ? 0 : Math.min(currentPage * rowsPerPage, totalRows);
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + maxButtons - 1);
    const normalizedStart = Math.max(1, end - maxButtons + 1);
    for (let i = normalizedStart; i <= end; i += 1) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  useEffect(() => {
    setPage((prev) => {
      const next = Math.min(prev, totalPages);
      return next < 1 ? 1 : next;
    });
  }, [totalPages]);

  useEffect(() => {
    setPage(1);
  }, [inventoryView]);

  const handleSearch = (event) => setQuery(event?.target?.value || "");
  const handleFilterSelect = (nextSelected) => {
    const raw = Array.isArray(nextSelected) ? nextSelected : nextSelected ? [nextSelected] : [];
    let next = new Set(raw.filter((option) => filterOptions.includes(option)));

    if (next.has("All")) {
      if (statusFilters.includes("All") && next.size > 1) {
        next.delete("All");
      } else {
        setStatusFilters(["All"]);
        return;
      }
    }

    const pickLast = (predicate) => {
      let last = null;
      raw.forEach((option) => {
        if (predicate(option)) last = option;
      });
      return last;
    };

    const lastAvailability = pickLast(isAvailabilityFilter);
    const lastExpiry = pickLast(isExpiryFilter);

    if (lastAvailability) {
      availabilityOptions.forEach((option) => {
        if (option !== lastAvailability) next.delete(option);
      });
    } else {
      availabilityOptions.forEach((option) => next.delete(option));
    }

    if (lastExpiry) {
      expiryOptions.forEach((option) => {
        if (option !== lastExpiry) next.delete(option);
      });
    } else {
      expiryOptions.forEach((option) => next.delete(option));
    }

    setStatusFilters(Array.from(next));
  };

  const normalizeProductId = useCallback((value) => {
    if (!value) return undefined;
    if (typeof value === "string") return value;
    if (value?.$oid) return value.$oid;
    if (typeof value === "object" && typeof value.toString === "function") {
      const str = value.toString();
      return str === "[object Object]" ? undefined : str;
    }
    return undefined;
  }, []);

  const getProductId = useCallback(
    (row) => {
      const product = row?.product ?? row;
      return (
        normalizeProductId(product?.id) ||
        normalizeProductId(product?._id) ||
        product?.refCode ||
        row?.refCode ||
        product?.sku ||
        product?.barcode
      );
    },
    [normalizeProductId]
  );


  const handleSelectionChange = useCallback(
    (viewKey) => (payload = {}) => {
      const ids = payload.ids instanceof Set ? payload.ids : new Set(payload.ids || []);
      const payloadRows = Array.isArray(payload.rows) ? payload.rows : [];
      setSelection((prev) => ({ ...prev, [viewKey]: ids }));
      setSelectionRows((prev) => ({ ...prev, [viewKey]: payloadRows }));
      setActionError("");
    },
    []
  );

  const currentSelection = selection[inventoryView] || new Set();
  const currentSelectionRows = selectionRows[inventoryView] || [];
  const hasSelection = (currentSelection?.size || 0) > 0;
  const handleOpenBarcodeModalClick = () => {
    const ids = Array.from(currentSelection || []);
    onOpenBarcodeModal?.({ preselectIds: ids });
  };

  const resetSelection = () => {
    setSelection({ active: new Set(), archived: new Set() });
    setSelectionRows({ active: [], archived: [] });
    setSelectionResetKey((prev) => prev + 1);
  };

  const handleViewChange = (nextView) => {
    if (nextView === inventoryView) return;
    setInventoryView(nextView);
    setActionError("");
    resetSelection();
  };

  const applyLocalStatus = useCallback(
    (ids, restore) => {
      if (!onRowsUpdate) return;
      const idSet = new Set(ids);
      const now = new Date().toISOString();
      setLocalRows((prevRows = []) =>
        prevRows.map((row) => {
          const id = getProductId(row);
          if (!idSet.has(id)) return row;
          const product = row.product ?? row;
          const nextProduct = {
            ...product,
            archivedAt: restore ? undefined : product?.archivedAt || now,
            status: restore ? "available" : "inactive",
            lifecycleStatus: restore ? "active" : "inactive",
          };
          const nextRow = {
            ...row,
            product: nextProduct,
            archived: !restore,
          };
          const availability = getAvailability(nextRow);
          const nextStatus = toStatusCell(availability);

          return {
            ...nextRow,
            availability,
            status: nextStatus,
          };
        })
      );

      onRowsUpdate?.((prevRows = []) =>
        prevRows.map((row) => {
          const id = getProductId(row);
          if (!idSet.has(id)) return row;
          const product = row.product ?? row;
          const nextProduct = {
            ...product,
            archivedAt: restore ? undefined : product?.archivedAt || now,
            status: restore ? "available" : "inactive",
            lifecycleStatus: restore ? "active" : "inactive",
          };
          const nextRow = {
            ...row,
            product: nextProduct,
            archived: !restore,
          };
          const availability = getAvailability(nextRow);
          const nextStatus = toStatusCell(availability);

          return {
            ...nextRow,
            availability,
            status: nextStatus,
          };
        })
      );
    },
    [getAvailability, getProductId, onRowsUpdate, toStatusCell]
  );

  const applyArchiveAction = useCallback(
    async ({ rows: targetRows = [], restore = false }) => {
      const ids = targetRows.map(getProductId).filter(Boolean);
      if (!ids.length) {
        setActionError("No products selected.");
        return;
      }

      setActionLoading(true);
      setActionError("");
      try {
        const mutateProducts = restore ? bulkRestoreProducts : bulkArchiveProducts;
        const result = await mutateProducts(ids);
        if (!result?.updated) {
          throw new Error("No products were updated. Please try again.");
        }
        const targetView = restore ? "active" : "archived";
        applyLocalStatus(ids, restore);
        resetSelection();
        setInventoryView(targetView);
        const count = ids.length;
        const verb = restore ? "Restored" : "Archived";
        notify(`${verb} ${count} product${count === 1 ? "" : "s"}.`, "success");
        const refreshedRows = await status?.onReload?.({ silent: false });
        if (Array.isArray(refreshedRows)) {
          setLocalRows(refreshedRows);
        }
        setInventoryView(targetView);
      } catch (err) {
        setActionError(err?.message || "Unable to update products.");
      } finally {
        setActionLoading(false);
      }
    },
    [applyLocalStatus, getProductId, notify, status]
  );

  const handleArchiveSelected = () =>
    applyArchiveAction({ rows: currentSelectionRows, restore: inventoryView === "archived" });

  const handleArchiveRow = (row) => applyArchiveAction({ rows: [row], restore: isArchivedRow(row) });
  const handleRestoreRow = (row) => applyArchiveAction({ rows: [row], restore: true });
  const handlePageChange = useCallback(
    (nextPage) => {
      setPage((prev) => {
        const target = Math.min(Math.max(nextPage, 1), totalPages);
        return prev === target ? prev : target;
      });
    },
    [totalPages]
  );

  const handleRowsPerPageChange = useCallback((event) => {
    const value = Number(event?.target?.value) || PAGE_SIZE_OPTIONS[0];
    setRowsPerPage(value);
    setPage(1);
  }, []);

  const normalizeId = useCallback((value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (value?.$oid) return value.$oid;
    return value.toString?.() || "";
  }, []);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const categories = await fetchCategories();
        const catMap = new Map();
        (categories || []).forEach((cat) => {
          const id = normalizeId(cat.id || cat._id);
          if (id) catMap.set(id, cat.name || cat.title || "");
        });
        setCategoryLookup(catMap);
      } catch (_err) {
        setCategoryLookup(new Map());
      }

      try {
        const suppliers = await fetchSuppliers({ includeArchived: true });
        const supMap = new Map();
        (suppliers || []).forEach((sup) => {
          const id = normalizeId(sup.id || sup._id);
          if (id) supMap.set(id, sup.name || sup.title || "");
        });
        setSupplierLookup(supMap);
      } catch (_err) {
        setSupplierLookup(new Map());
      }
    };

    loadLookups();
  }, [normalizeId]);

  const formatExportFilename = useCallback(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, "0");
    const month = now.toLocaleString("en", { month: "short" });
    const year = String(now.getFullYear()).slice(-2);
    return `inventory_${day}${month}${year}`;
  }, []);

  const normalizeExportRow = useCallback(
    (row) => {
      const product = row?.product ?? row ?? {};
      const categories = (() => {
        const names = new Set();
        if (Array.isArray(product.categories)) {
          product.categories.forEach((cat) => {
            if (!cat) return;
            if (typeof cat === "string") {
              const name = categoryLookup.get(cat) || "";
              if (name) names.add(name);
              return;
            }
            const id = normalizeId(cat.id || cat._id);
            if (cat.name) names.add(cat.name);
            else if (id && categoryLookup.get(id)) names.add(categoryLookup.get(id));
          });
        }
        const catIds = Array.isArray(product.categoryIds)
          ? product.categoryIds
          : product.categoryId
            ? [product.categoryId]
            : [];
        catIds.forEach((cid) => {
          const name = categoryLookup.get(normalizeId(cid));
          if (name) names.add(name);
        });
        return Array.from(names);
      })();

      const supplierId = product.supplierId || product.supplier?.id || product.supplier?._id;
      const supplierName =
        product.supplier?.name || supplierLookup.get(normalizeId(supplierId)) || product.supplierName || "";

      return {
        Name: product.name || row?.name || "Untitled",
        "Ref Code": product.refCode || row?.refCode || product.sku || "",
        Barcode: product.barcode || "",
        Description: product.description || "",
        Price: product.defaultPrice ?? row?.price ?? "",
        Cost: product.costPrice ?? "",
        Quantity: product.quantityOnHand ?? row?.quantity ?? "",
        "Stock Alert": product.reorderLevel ?? "",
        Status: row?.status?.label || product.status || "",
        "Lifecycle Status": product.lifecycleStatus || "",
        "Expiry Date": product.expiryDate || row?.expiry || "",
        Categories: categories.join(", "),
        Supplier: supplierName,
        "Created At": product.createdAt || "",
        "Updated At": product.updatedAt || "",
      };
    },
    [categoryLookup, normalizeId, supplierLookup]
  );

  const handleExport = useCallback(
    (format = "xlsx") => {
      const data = exportRows.map(normalizeExportRow);
      const sheet = XLSX.utils.json_to_sheet(data);
      const filename = `${formatExportFilename()}.${format === "csv" ? "csv" : "xlsx"}`;

      if (format === "csv") {
        const csv = XLSX.utils.sheet_to_csv(sheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
        return;
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Inventory");
      const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    },
    [exportRows, formatExportFilename, normalizeExportRow]
  );

  const archiveButtonLabel = inventoryView === "archived" ? "Restore" : "Archive";
  const archiveDisabled = !hasSelection || actionLoading;

  return (
    <>
      {isLoading ? (
        <div className="inline-alert info">
          <span>Loading inventory from the backend…</span>
        </div>
      ) : null}

      {hasError ? (
        <div className="inline-alert error">
          <div>
            <strong>Inventory failed to load.</strong> {status.error}
          </div>
          {status?.onReload ? (
            <button className="ghost-btn small" type="button" onClick={status.onReload}>
              Retry
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="inventory-view-toggle-row">
        <div className="inventory-view-toggle" role="tablist" aria-label="Inventory views">
          <button
            type="button"
            role="tab"
            aria-selected={inventoryView === "active"}
            className={inventoryView === "active" ? "active" : ""}
            onClick={() => handleViewChange("active")}
          >
            Active Inventory
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={inventoryView === "archived"}
            className={inventoryView === "archived" ? "active" : ""}
            onClick={() => handleViewChange("archived")}
          >
            Archived Inventory
          </button>
        </div>
      </div>

      <section className="panel wide">
        <header className="panel-header">
          <div className="panel-title">
            <div className="quick-actions" aria-label="Actions">
              <div className="quick-actions-menu">
                <button
                  className="quick-action-btn"
                  type="button"
                  disabled={archiveDisabled}
                  onClick={handleArchiveSelected}
                >
                  {archiveButtonLabel}
                </button>
                <button
                  className="primary-btn small"
                  type="button"
                  onClick={handleOpenBarcodeModalClick}
                  disabled={!onOpenBarcodeModal || actionLoading}
                  style={{ marginLeft: "8px" }}
                >
                  Print barcodes
                </button>
              </div>
            </div>
          </div>
          <div className="panel-actions">
            <FilterBar options={filterOptions} selected={statusFilters} onSelect={handleFilterSelect} allowMultiple />
            <div className="export-menu" style={{ position: "relative" }}>
              <button className="ghost-btn small" type="button" onClick={() => setShowExportMenu((prev) => !prev)}>
                Export ▾
              </button>
              {showExportMenu ? (
                <div
                  className="panel menu-card"
                  style={{
                    position: "absolute",
                    right: 0,
                    top: "calc(100% + 6px)",
                    padding: "8px",
                    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                    zIndex: 5,
                    minWidth: "160px",
                  }}
                >
                  <button
                    type="button"
                    className="ghost-btn subtle wide"
                    onClick={() => {
                      handleExport("xlsx");
                      setShowExportMenu(false);
                    }}
                  >
                    Excel (.xlsx)
                  </button>
                  <button
                    type="button"
                    className="ghost-btn subtle wide"
                    onClick={() => {
                      handleExport("csv");
                      setShowExportMenu(false);
                    }}
                  >
                    CSV (.csv)
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>
        {actionError ? <div className="inline-alert error compact">{actionError}</div> : null}
        {actionLoading ? <div className="inline-alert info compact">Updating products…</div> : null}

        <div className="table-search">
          <div className="table-search-inner">
            <span className="search-icon" aria-hidden="true">
              🔍
            </span>
            <input
              type="search"
              placeholder="Search by name, ref code, or barcode"
              aria-label="Search by name, ref code, or barcode"
              value={query}
              onChange={handleSearch}
            />
          </div>
        </div>

        <div className="table-wrap">
          <Table
            columns={config.columns}
            rows={paginatedRows}
            onRowClick={onRowClick}
            onSelectionChange={handleSelectionChange(inventoryView)}
            resetSelectionKey={`${inventoryView}-${selectionResetKey}`}
            onArchiveRow={handleArchiveRow}
            onRestoreRow={handleRestoreRow}
          />
        </div>

        <footer className="panel-footer">
          <div className="muted small">
            {totalRows === 0 ? "No results to display" : `Showing ${pageStart}-${pageEnd} of ${totalRows}`}
          </div>
          <div className="pagination">
            <button type="button" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
              «
            </button>
            <button type="button" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              ‹
            </button>
            {pageNumbers.map((pageNumber) => (
              <button
                type="button"
                key={pageNumber}
                className={pageNumber === currentPage ? "current" : ""}
                onClick={() => handlePageChange(pageNumber)}
                disabled={pageNumber === currentPage}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalRows === 0}
            >
              ›
            </button>
            <button
              type="button"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || totalRows === 0}
            >
              »
            </button>
          </div>
          <div className="muted small">
            Show{" "}
            <select aria-label="rows per page" value={rowsPerPage} onChange={handleRowsPerPageChange}>
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option value={option} key={option}>
                  {option}
                </option>
              ))}
            </select>{" "}
            / page
          </div>
        </footer>
      </section>
    </>
  );
}

export default InventoryPage;
